import * as React from 'react';
import classNames from 'classnames';
import { ClickOutside, SearchInput, EmptyPlaceholder } from '../common';
import { isIE } from '../../utils/isIE.utils';
import { Checkbox } from './Checkbox';
import { Chip } from './Chip';
import { arrayUtils } from '../../utils/array.utils';
import { constants } from '../../constants';

export interface MultiSelectDataItem {
    text: string;
    value: string | number;
    disabled?: boolean;
    selected?: boolean;
    groupKey?: string;
}

export enum MultiselectDisplayType {
    Default = 'Default',
    ChipList = 'ChipList',
}

export interface MultiSelectProps {
    name?: string
    required?: boolean
    className?: string
    placeholder?: string
    disabled?: boolean
    sourceItems: MultiSelectDataItem[]
    onItemSelect: (item: MultiSelectDataItem, selected: boolean) => void
    onGenerateSelectedText?: (selectedItems: MultiSelectDataItem[]) => React.ReactNode
    showAddButton: boolean,
    onResetAll?: (e: React.MouseEvent) => void,
    onSelectAll?: (e: React.MouseEvent) => void,
    showResetButton?: boolean,
    showSelectAllButton?: boolean,
    selectButtonText?: string,
    resetButtonText?: string,
    withSearch?: boolean,
    searchPlaceholder?: string,
    displayType?: MultiselectDisplayType
}

export function MultiSelect(
    {
        name,
        required = false,
        placeholder = '',
        disabled = false,
        sourceItems,
        onItemSelect,
        showAddButton = true,
        onGenerateSelectedText,
        className = '',
        onResetAll,
        onSelectAll,
        showResetButton = false,
        showSelectAllButton = false,
        selectButtonText = 'Select All',
        resetButtonText = 'Reset to default',
        withSearch = false,
        searchPlaceholder = 'Start typing...',
        displayType = MultiselectDisplayType.Default
    }: MultiSelectProps) {
    const [expanded, setExpanded] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState('');
    const selectedItems = sourceItems.filter(i => i.selected);

    const trimmedSearch = searchTerm.trim().toLowerCase();
    const filteredItems = withSearch && trimmedSearch
        ? sourceItems.filter(i =>
            i.text.toLowerCase().includes(trimmedSearch) ||
            !!i.groupKey?.toLowerCase().includes(trimmedSearch))
        : sourceItems;

    const collapse = () => {
        setExpanded(false);
        setSearchTerm('');
    };

    const getText = (): React.ReactNode => {
        if (selectedItems.length) {
            if (onGenerateSelectedText != null) {
                return onGenerateSelectedText(selectedItems);
            }

            if (displayType === MultiselectDisplayType.ChipList) {
                return (
                    <div className="chip-list">
                        {selectedItems.map(item =>
                            <Chip
                                key={item.value}
                                disabled={!!item.disabled || disabled}
                                text={item.text}
                                onClick={() => { }}
                                onDelete={() => onItemSelect(item, false)}
                            />
                        )}
                    </div>
                );
            }

            return selectedItems.map(s => s.text).join(', ');
        }

        return <span className="placeholder">{placeholder}</span>;
    };

    const multiselectCssClasses = classNames(className, {
        'form-control form-select multiselect': true,
        show: expanded,
        disabled: disabled,
        selected: sourceItems.filter(i => i.selected).length > 0,
        multiselected: sourceItems.filter(i => i.selected).length > 1,
        'chip-list-selected-content': displayType === MultiselectDisplayType.ChipList,
    });

    const renderResetSelectButtons = () => {
        if (showSelectAllButton) {
            return (
                <li className="reset-select">
                    <button className="btn btn-link" onClick={selectedItems.length > 0 ? onResetAll : onSelectAll}>
                        {selectedItems.length > 0 ? resetButtonText : selectButtonText}
                    </button>
                </li>
            );
        }

        if (showResetButton) {
            return (
                <li className="reset-select">
                    <button disabled={!selectedItems.length} className="btn btn-link" onClick={onResetAll}>
                        {resetButtonText}
                    </button>
                </li>
            );
        }
    }

    function renderGroupedItems() {
        const grouped = arrayUtils.groupBy(filteredItems, i => i.groupKey);

        return [...grouped.entries()].map(([groupKey, items], index) => {
            return <MultiSelectGroup key={index} groupKey={groupKey} items={items} onItemSelect={onItemSelect} />;
        });
    }

    return (
        <ClickOutside
            onClick={() => isIE() ? setTimeout(collapse, 0) : collapse()}
        >
            <input
                type="text"
                name={name}
                required={required}
                value={selectedItems.length || ''}
                onChange={() => true}
                className="invisible"
            />
            <div
                className={multiselectCssClasses}
                onClick={disabled ? undefined : () => expanded ? collapse() : setExpanded(true)}
            >
                {getText()}
            </div>
            {
                expanded && (
                    <div className={classNames('multiselect-list', { 'multiselect-list-with-search': withSearch })}>
                        {withSearch && (
                            <div className="multiselect-list-search">
                                <SearchInput
                                    placeholder={searchPlaceholder}
                                    value={searchTerm}
                                    onChange={setSearchTerm}
                                    onClear={() => setSearchTerm('')}
                                />
                            </div>
                        )}
                        <ul>
                            {renderResetSelectButtons()}
                            {filteredItems.some(x => x.groupKey != null)
                                ? renderGroupedItems()
                                : filteredItems.map(i => <MultiSelectItem key={i.value} item={i} onItemSelect={onItemSelect} />)}
                        </ul>
                        {withSearch && !filteredItems.length && <EmptyPlaceholder textView />}
                        {
                            showAddButton && (
                                <div className="button-wrapper">
                                    <button className="btn btn-main btn-sm" onClick={collapse}>Add</button>
                                </div>
                            )
                        }
                    </div>
                )
            }
        </ClickOutside>
    );
}


interface MultiSelectItemProps {
    item: MultiSelectDataItem;
    onItemSelect: (item: MultiSelectDataItem, selected: boolean) => void;
}
function MultiSelectItem({ item, onItemSelect }: MultiSelectItemProps) {
    return (
        <li key={item.value}>
            <Checkbox
                onChange={e => onItemSelect(item, e.target.checked)}
                checked={!!item.selected}
                disabled={!!item.disabled}
                label={item.text}
            />
        </li>
    );
}

interface MultiSelectGroupProps {
    groupKey: string | undefined;
    items: MultiSelectDataItem[];
    onItemSelect: (item: MultiSelectDataItem, selected: boolean) => void;
}
function MultiSelectGroup({ groupKey, items, onItemSelect }: MultiSelectGroupProps) {
    return (
        <div>
            <div className="section-header">{groupKey || constants.emptyPlaceholder}</div>
            {items.map(i => <MultiSelectItem key={i.value} item={i} onItemSelect={onItemSelect} />)}
        </div>
    )
}
